import { createContext, useContext, useEffect, useRef } from 'react';

import {
  Form,
  Formik,
  FormikConfig,
  FormikValues,
  useFormikContext,
  yupToFormErrors,
} from 'formik';
import { reach, ObjectSchema, AnySchema } from 'yup';

export const ValidationSchemaContext = createContext<ObjectSchema<any> | null>(
  null,
);

export const useValidationSchema = () => {
  const ctx = useContext(ValidationSchemaContext);

  const safeReach = (schema: AnySchema, path: string): AnySchema | null => {
    try {
      const result = reach(schema, path);
      return (result as AnySchema) ?? null;
    } catch {
      return null;
    }
  };

  const getFieldSchema = (name: string) => {
    if (!ctx) return undefined;

    try {
      const fieldSchema = safeReach(ctx, name);
      return fieldSchema?.describe?.();
    } catch (err) {
      console.warn(`getFieldSchema failed for "${name}"`, err);
      return undefined;
    }
  };

  const isFieldRequired = (name: string) => {
    if (!ctx) return false;

    try {
      const fieldSchema = safeReach(ctx, name);
      const description = fieldSchema?.describe?.();

      return (
        (description?.tests?.some((test) => test.name === 'required') ||
          !description?.optional) ??
        false
      );
    } catch (err) {
      console.warn(`isFieldRequired failed for "${name}"`, err);
      return false;
    }
  };

  return {
    schema: ctx,
    getFieldSchema,
    isFieldRequired,
  };
};

export type AppFormProps<T> = FormikConfig<T> & {
  onInput?: (values: FormikValues) => void;
  // validationSchema?: any;
  // добавляем: если передали - используем её, чтобы выбрать схему из values
  validationSchemaSelector?: (values: T) => any;
  validationContext?: any;
};

export const AppForm = <T extends FormikValues>({
  validationSchema,
  validationContext,
  children,
  onInput,
  validationSchemaSelector,
  ...rest
}: AppFormProps<T>) => {
  return (
    <Formik<T>
      validationSchema={validationSchema}
      validate={async (values) => {
        const schema = validationSchemaSelector?.(values) ?? validationSchema;

        if (!schema) return {};

        try {
          await schema.validate(values, {
            abortEarly: false,
            context: validationContext,
          });
          return {};
        } catch (err: any) {
          return yupToFormErrors(err);
        }
      }}
      {...rest}
    >
      {(formikProps) => {
        const content =
          typeof children === 'function' ? children(formikProps) : children;

        const effectiveSchema =
          validationSchemaSelector?.(formikProps.values) ?? validationSchema;

        return (
          <ValidationSchemaContext.Provider value={effectiveSchema}>
            <Form>
              {onInput && <FormikWatcher onChange={onInput} />}

              {/*<MemoizedChildren children={content} />*/}
              {content}
            </Form>
          </ValidationSchemaContext.Provider>
        );
      }}
    </Formik>
  );
};

const FormikWatcher = ({ onChange }: { onChange: (values: any) => void }) => {
  const { values } = useFormikContext();
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current(values);
  }, [values]);

  return null;
};
